import { Api } from "services/api/api.service";
import { PayoutType } from "model/Tip";
import { formatNumber } from "utils/utils";
import {
  cancelManualPayout,
  mergeTips,
  payoutTipBatch,
} from "../logic/tips.v2.logic";
import type {
  TipPayoutActions,
  UseTipPayoutActionsProps,
} from "../tips.component.types";

const TIPS_PAYOUT_MESSAGE_TO_KEY: Record<string, string> = {
  "Insufficient company credits": "Tips.InsufficientCompanyCredits",
};

const resolveTipsPayoutErrorMessage = ({
  backendMessage,
  fallbackKey,
  t,
}: {
  readonly backendMessage: string | null;
  readonly fallbackKey: string;
  readonly t: (key: string) => string;
}): string => {
  if (!backendMessage) {
    return t(fallbackKey);
  }

  const mappedKey = TIPS_PAYOUT_MESSAGE_TO_KEY[backendMessage];
  return mappedKey ? t(mappedKey) : t(fallbackKey);
};

const getSelectedTipAmount = ({
  companyTipIds,
  tips,
}: {
  readonly companyTipIds: readonly number[];
  readonly tips: UseTipPayoutActionsProps["tips"];
}): number =>
  tips
    .filter((tip) => companyTipIds.includes(tip.companyTipId))
    .reduce((sum, tip) => sum + tip.amount, 0);

export const useTipPayoutActions = (
  props: UseTipPayoutActionsProps,
): TipPayoutActions => {
  const handlePayoutTips = async (
    companyTipIds: readonly number[],
    payoutType: PayoutType,
  ) => {
    if (props.companyId === null || companyTipIds.length === 0) {
      return { errorMessage: null, ok: false };
    }

    if (payoutType !== PayoutType.Automatic) {
      props.setErrorMessage(null);
    }

    if (payoutType === PayoutType.Automatic) {
      try {
        const totalAmount = getSelectedTipAmount({
          companyTipIds,
          tips: props.tips,
        });
        const creditBalanceResponse =
          await Api().companyCredits.getCreditBalance(props.companyId);
        const balance = creditBalanceResponse.data.amount;

        if (balance < totalAmount) {
          return {
            errorMessage: props.t("Tips.NotEnoughCredit", {
              formattedAmountNeeded: formatNumber(totalAmount),
              formattedBalance: formatNumber(balance),
              formattedMissingAmount: formatNumber(totalAmount - balance),
            }),
            ok: false,
          };
        }
      } catch (_error: unknown) {
        // Fall back to the payout request so backend can still return a concrete error.
      }
    }

    const result = await payoutTipBatch({
      companyId: props.companyId,
      companyTipIds,
      payoutType,
    });

    if (result.ok) {
      props.setTips((currentTips) =>
        mergeTips({ currentTips, nextTips: result.value }),
      );
      return { errorMessage: null, ok: true };
    }

    switch (result.error.type) {
      case "payout-missing-swish-number":
        return {
          errorMessage: resolveTipsPayoutErrorMessage({
            backendMessage: result.error.backendMessage,
            fallbackKey: "Tips.MissingSwishNumber",
            t: props.t,
          }),
          ok: false,
        };
      case "payout-failed": {
        const errorMessage = resolveTipsPayoutErrorMessage({
          backendMessage: result.error.backendMessage,
          fallbackKey: "Tips.UnexpectedError",
          t: props.t,
        });

        if (payoutType !== PayoutType.Automatic) {
          props.setErrorMessage(errorMessage);
        }

        return {
          errorMessage,
          ok: false,
        };
      }
    }
  };

  const handleRevertManualPayout = async (
    companyTipIds: readonly number[],
  ): Promise<void> => {
    if (props.companyId === null || companyTipIds.length === 0) return;

    props.setErrorMessage(null);

    const result = await cancelManualPayout({
      companyId: props.companyId,
      companyTipIds,
    });

    if (result.ok) {
      props.setTips((currentTips) =>
        mergeTips({ currentTips, nextTips: result.value }),
      );
      return;
    }

    props.setErrorMessage(props.t("Tips.UnexpectedError"));
  };

  return {
    handlePayoutTips,
    handleRevertManualPayout,
  };
};
